import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { usePractice } from '../context/PracticeContext';
import Tooltip from '../components/Tooltip';

function Templates() {
  const { selectedPracticeId, selectedPractice } = usePractice();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    campaignType: 'reactivation',
    channel: 'sms',
    templateName: '',
    subject: '',
    content: '',
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({
    campaignType: '',
    channel: '',
  });

  useEffect(() => {
    if (selectedPracticeId) {
      loadTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPracticeId, filters]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const query = new URLSearchParams();
      query.append('practice_id', selectedPracticeId);
      if (filters.campaignType) query.append('campaign_type', filters.campaignType);
      if (filters.channel) query.append('channel', filters.channel);

      const response = await api.get(`/templates?${query.toString()}`);
      setTemplates(response.data || []);
    } catch (err) {
      setError('Failed to load templates');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    // Validation
    const errors = [];
    if (!formData.templateName) errors.push('Template name is required');
    if (!formData.content) errors.push('Content is required');
    if (formData.channel === 'email' && !formData.subject) {
      errors.push('Subject is required for email templates');
    }

    if (errors.length > 0) {
      setFormError(errors.join(', '));
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        await api.put(`/templates/${editingTemplate.id}`, {
          templateName: formData.templateName,
          subject: formData.subject,
          content: formData.content,
        });
      } else {
        await api.post('/templates', {
          practiceId: selectedPracticeId,
          ...formData,
        });
      }
      setShowForm(false);
      setEditingTemplate(null);
      resetForm();
      loadTemplates();
    } catch (err) {
      setFormError(err.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      campaignType: 'reactivation',
      channel: 'sms',
      templateName: '',
      subject: '',
      content: '',
    });
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      campaignType: template.campaign_type,
      channel: template.channel,
      templateName: template.template_name,
      subject: template.subject || '',
      content: template.content,
    });
    setShowForm(true);
    setFormError('');
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Are you sure you want to delete "${template.template_name}"?`)) {
      return;
    }

    try {
      await api.delete(`/templates/${template.id}`);
      loadTemplates();
    } catch (err) {
      alert(err.message || 'Failed to delete template');
    }
  };

  const handleToggleActive = async (template) => {
    try {
      if (template.is_active) {
        await api.post(`/templates/${template.id}/deactivate`);
      } else {
        await api.post(`/templates/${template.id}/activate`);
      }
      loadTemplates();
    } catch (err) {
      alert(err.message || 'Failed to update template status');
    }
  };

  const handleCreateDefaults = async () => {
    if (!window.confirm('This will create default templates for all campaign types. Continue?')) {
      return;
    }

    try {
      await api.post(`/templates/practice/${selectedPracticeId}/defaults`, {
        practiceName: selectedPractice?.name || 'Our Practice',
      });
      loadTemplates();
    } catch (err) {
      alert(err.message || 'Failed to create default templates');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingTemplate(null);
    resetForm();
    setFormError('');
  };

  const getCampaignLabel = (type) => {
    const labels = {
      'reactivation': 'Reactivation',
      'no-show': 'No-Show',
      'cancellation': 'Cancellation',
    };
    return labels[type] || type;
  };

  if (!selectedPracticeId) {
    return (
      <div className="card">
        <h2>Campaign Message Templates</h2>
        <p style={{ color: '#666' }}>Please select a practice to manage templates.</p>
      </div>
    );
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div>
      <div className="card">
        <div style={styles.header}>
          <div>
            <h2>Campaign Message Templates</h2>
            <p style={{ color: '#666', marginTop: '4px' }}>
              Manage SMS and email templates for {selectedPractice?.name || 'selected practice'}
            </p>
          </div>
          <div style={styles.headerActions}>
            {!showForm && (
              <>
                <button
                  className="button secondary"
                  onClick={handleCreateDefaults}
                >
                  Create Defaults
                </button>
                <button
                  className="button"
                  onClick={() => {
                    setShowForm(true);
                    setEditingTemplate(null);
                    resetForm();
                  }}
                >
                  + Add Template
                </button>
              </>
            )}
          </div>
        </div>

        {showForm && (
          <div style={styles.formContainer}>
            <h3>{editingTemplate ? 'Edit Template' : 'New Template'}</h3>
            {formError && <div className="error">{formError}</div>}
            <form onSubmit={handleSubmit}>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Campaign Type * <Tooltip text="Select which campaign this template applies to" /></label>
                  <select
                    value={formData.campaignType}
                    onChange={(e) => setFormData({ ...formData, campaignType: e.target.value })}
                    style={styles.select}
                    disabled={saving || editingTemplate}
                  >
                    <option value="reactivation">Reactivation</option>
                    <option value="no-show">No-Show</option>
                    <option value="cancellation">Cancellation</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Channel * <Tooltip text="SMS for text messages, Email for email campaigns" /></label>
                  <select
                    value={formData.channel}
                    onChange={(e) => setFormData({ ...formData, channel: e.target.value })}
                    style={styles.select}
                    disabled={saving || editingTemplate}
                  >
                    <option value="sms">SMS</option>
                    <option value="email">Email</option>
                  </select>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Template Name *</label>
                <input
                  type="text"
                  value={formData.templateName}
                  onChange={(e) => setFormData({ ...formData, templateName: e.target.value })}
                  style={styles.input}
                  placeholder="E.g., Reactivation SMS - Initial"
                  disabled={saving}
                />
              </div>

              {formData.channel === 'email' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Subject *</label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    style={styles.input}
                    placeholder="Email subject line"
                    disabled={saving}
                  />
                </div>
              )}

              <div style={styles.formGroup}>
                <label style={styles.label}>Content * <Tooltip text="Use {{first_name}}, {{last_name}}, {{booking_url}}, {{practice_name}} for personalization" /></label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  style={styles.textarea}
                  rows={formData.channel === 'email' ? 8 : 4}
                  placeholder="Message content. Use {{first_name}}, {{booking_url}} for personalization."
                  disabled={saving}
                />
                <div style={styles.hint}>
                  Available variables: {'{{patient_first_name}}'}, {'{{patient_last_name}}'}, {'{{appointment_date}}'}, {'{{appointment_time}}'}, {'{{appointment_type}}'}, {'{{provider_name}}'}, {'{{department_name}}'}, {'{{practice_name}}'}, {'{{practice_phone}}'}, {'{{intake_link}}'}, {'{{telehealth_link}}'}, {'{{booking_url}}'}
                </div>
              </div>

              <div style={styles.formActions}>
                <button type="submit" className="button" disabled={saving}>
                  {saving ? 'Saving...' : editingTemplate ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  className="button secondary"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="filters" style={{ marginBottom: '20px' }}>
          <select
            value={filters.campaignType}
            onChange={(e) => setFilters({ ...filters, campaignType: e.target.value })}
          >
            <option value="">All Campaign Types</option>
            <option value="reactivation">Reactivation</option>
            <option value="no-show">No-Show</option>
            <option value="cancellation">Cancellation</option>
          </select>
          <select
            value={filters.channel}
            onChange={(e) => setFilters({ ...filters, channel: e.target.value })}
          >
            <option value="">All Channels</option>
            <option value="sms">SMS</option>
            <option value="email">Email</option>
          </select>
        </div>

        {loading ? (
          <div className="loading">Loading templates...</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Campaign</th>
                <th>Channel</th>
                <th>Content Preview</th>
                <th>Status <Tooltip text="Active templates are used in campaigns. Inactive templates are skipped." /></th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', color: '#666' }}>
                    No templates found. Create one or use "Create Defaults" to get started.
                  </td>
                </tr>
              ) : (
                templates.map((template) => (
                  <tr key={template.id}>
                    <td>
                      <strong>{template.template_name}</strong>
                      {template.channel === 'email' && template.subject && (
                        <div style={styles.subject}>Subject: {template.subject}</div>
                      )}
                    </td>
                    <td>
                      <span className="badge info">
                        {getCampaignLabel(template.campaign_type)}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${template.channel === 'sms' ? 'warning' : 'success'}`}>
                        {template.channel.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <div style={styles.contentPreview}>
                        {template.content.substring(0, 100)}
                        {template.content.length > 100 && '...'}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${template.is_active ? 'success' : 'danger'}`}>
                        {template.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div style={styles.actions}>
                        <button
                          style={styles.actionButton}
                          onClick={() => handleEdit(template)}
                        >
                          Edit
                        </button>
                        <button
                          style={{ ...styles.actionButton, background: template.is_active ? '#ffc107' : '#28a745' }}
                          onClick={() => handleToggleActive(template)}
                        >
                          {template.is_active ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          style={{ ...styles.actionButton, ...styles.dangerButton }}
                          onClick={() => handleDelete(template)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
  },
  headerActions: {
    display: 'flex',
    gap: '10px',
  },
  formContainer: {
    background: '#f8f9fa',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '20px',
  },
  formRow: {
    display: 'flex',
    gap: '16px',
  },
  formGroup: {
    marginBottom: '16px',
    flex: 1,
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontWeight: '500',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  hint: {
    marginTop: '6px',
    fontSize: '12px',
    color: '#666',
  },
  formActions: {
    display: 'flex',
    gap: '10px',
  },
  subject: {
    fontSize: '12px',
    color: '#666',
    marginTop: '4px',
  },
  contentPreview: {
    fontSize: '13px',
    color: '#555',
    maxWidth: '300px',
  },
  actions: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
  },
  actionButton: {
    padding: '5px 10px',
    fontSize: '12px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    background: '#667eea',
    color: '#fff',
  },
  dangerButton: {
    background: '#dc3545',
  },
};

export default Templates;
